/**
 * G2eGente - Resultados do Diagnóstico NR01
 * Dashboard com gráficos, mapa de riscos e recomendações IA
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { nr01API } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Separator } from '../../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  ArrowLeft,
  BarChart3,
  Users,
  Percent,
  ShieldCheck,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  AlertCircle,
  Building2,
  Brain,
  FileText,
  Download,
  Sparkles,
  Printer
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Cell
} from 'recharts';
import jsPDF from 'jspdf';

const RISK_COLORS = {
  baixo: '#22c55e',
  medio: '#eab308',
  alto: '#ef4444'
};

const RISK_LABELS = {
  baixo: 'Baixo',
  medio: 'Médio',
  alto: 'Alto'
};

export default function NR01Results() {
  const navigate = useNavigate();
  const { diagnosticId } = useParams();
  const [loading, setLoading] = useState(true);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [results, setResults] = useState(null);
  const [recommendations, setRecommendations] = useState(null);

  useEffect(() => {
    fetchResults();
  }, [diagnosticId]);

  const fetchResults = async () => {
    try {
      const { data } = await nr01API.getResults(diagnosticId);
      setResults(data);
      
      // Check if recommendations already exist
      if (data.diagnostic?.recomendacoes) {
        setRecommendations(data.diagnostic.recomendacoes);
      }
    } catch (error) {
      toast.error('Erro ao carregar resultados');
      navigate('/company/nr01');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateRecommendations = async () => {
    setGeneratingAI(true);
    try {
      const { data } = await nr01API.generateRecommendations(diagnosticId);
      if (data.success) {
        setRecommendations(data.recomendacoes);
        toast.success('Recomendações geradas com sucesso!');
      } else {
        toast.error('Erro ao gerar recomendações');
      }
    } catch (error) {
      toast.error('Erro ao gerar recomendações');
    } finally {
      setGeneratingAI(false);
    }
  };

  const [exportingPDF, setExportingPDF] = useState(false);
  
  const handleExportPDF = async () => {
    setExportingPDF(true);
    try {
      const { data } = await nr01API.exportPGR(diagnosticId);
      
      // Create PDF using jsPDF
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      let yPos = margin;
      
      // Helper function to add text with word wrap
      const addText = (text, x, y, maxWidth, fontSize = 10, fontStyle = 'normal') => {
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', fontStyle);
        const lines = doc.splitTextToSize(text, maxWidth);
        doc.text(lines, x, y);
        return y + (lines.length * fontSize * 0.4);
      };
      
      // Check if we need a new page
      const checkNewPage = (requiredHeight) => {
        if (yPos + requiredHeight > pageHeight - margin) {
          doc.addPage();
          yPos = margin;
          return true;
        }
        return false;
      };
      
      // Header
      doc.setFillColor(245, 158, 11); // Amber color
      doc.rect(0, 0, pageWidth, 35, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('RELATÓRIO DE AVALIAÇÃO DE RISCOS', pageWidth / 2, 15, { align: 'center' });
      doc.setFontSize(14);
      doc.text('PSICOSSOCIAIS - NR-01 / PGR', pageWidth / 2, 23, { align: 'center' });
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Gerado em: ${data.gerado_em}`, pageWidth / 2, 31, { align: 'center' });
      
      yPos = 45;
      doc.setTextColor(0, 0, 0);
      
      // Company Info
      doc.setFillColor(241, 245, 249);
      doc.rect(margin, yPos, pageWidth - 2 * margin, 25, 'F');
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('EMPRESA', margin + 5, yPos + 7);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(`Nome: ${data.empresa.nome}`, margin + 5, yPos + 14);
      doc.text(`CNPJ: ${data.empresa.cnpj || 'Não informado'}`, margin + 5, yPos + 20);
      yPos += 32;
      
      // Diagnostic Info
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('INFORMAÇÕES DO DIAGNÓSTICO', margin, yPos);
      yPos += 8;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(`Título: ${data.diagnostico.titulo}`, margin, yPos);
      yPos += 6;
      doc.text(`Metodologia: ${data.diagnostico.metodologia}`, margin, yPos);
      yPos += 6;
      doc.text(`Setores avaliados: ${data.diagnostico.setores_avaliados.join(', ')}`, margin, yPos);
      yPos += 6;
      doc.text(`Total de perguntas: ${data.diagnostico.total_perguntas}`, margin, yPos);
      yPos += 12;
      
      // Participation
      doc.setFillColor(236, 253, 245);
      doc.rect(margin, yPos, pageWidth - 2 * margin, 18, 'F');
      doc.setFont('helvetica', 'bold');
      doc.text('PARTICIPAÇÃO', margin + 5, yPos + 7);
      doc.setFont('helvetica', 'normal');
      doc.text(`Respostas: ${data.participacao.total_respostas} de ${data.participacao.total_elegivel} elegíveis (${data.participacao.taxa_adesao}% de adesão)`, margin + 5, yPos + 14);
      yPos += 26;
      
      // Risk Summary
      checkNewPage(40);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('RESUMO DOS RISCOS IDENTIFICADOS', margin, yPos);
      yPos += 8;
      
      if (data.resumo_riscos.alto_risco.length > 0) {
        doc.setFillColor(254, 226, 226);
        doc.rect(margin, yPos, pageWidth - 2 * margin, 12, 'F');
        doc.setTextColor(185, 28, 28);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.text(`RISCO ALTO (${data.resumo_riscos.quantidade_alto}): ${data.resumo_riscos.alto_risco.join(', ')}`, margin + 5, yPos + 8);
        yPos += 15;
      }
      
      if (data.resumo_riscos.medio_risco.length > 0) {
        doc.setFillColor(254, 249, 195);
        doc.rect(margin, yPos, pageWidth - 2 * margin, 12, 'F');
        doc.setTextColor(161, 98, 7);
        doc.setFont('helvetica', 'bold');
        doc.text(`RISCO MÉDIO (${data.resumo_riscos.quantidade_medio}): ${data.resumo_riscos.medio_risco.join(', ')}`, margin + 5, yPos + 8);
        yPos += 15;
      }
      
      doc.setTextColor(0, 0, 0);
      yPos += 5;
      
      // Detailed Analysis by Dimension
      checkNewPage(30);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('ANÁLISE POR DIMENSÃO', margin, yPos);
      yPos += 8;
      
      Object.values(data.analise_geral).forEach((dim) => {
        checkNewPage(20);
        
        // Color based on risk level
        if (dim.nivel_risco === 'alto') {
          doc.setFillColor(254, 226, 226);
        } else if (dim.nivel_risco === 'medio') {
          doc.setFillColor(254, 249, 195);
        } else {
          doc.setFillColor(220, 252, 231);
        }
        
        doc.rect(margin, yPos, pageWidth - 2 * margin, 14, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.text(`${dim.nome}`, margin + 5, yPos + 6);
        doc.setFont('helvetica', 'normal');
        doc.text(`Média: ${dim.media}/5 - Risco ${dim.nivel_risco.toUpperCase()}`, margin + 5, yPos + 11);
        yPos += 17;
      });
      
      yPos += 5;
      
      // Recommendations (if available)
      if (data.recomendacoes?.texto_pgr) {
        checkNewPage(40);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text('TEXTO PARA INCLUSÃO NO PGR', margin, yPos);
        yPos += 8;
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        const pgrLines = doc.splitTextToSize(data.recomendacoes.texto_pgr, pageWidth - 2 * margin);
        pgrLines.forEach(line => {
          checkNewPage(6);
          doc.text(line, margin, yPos);
          yPos += 5;
        });
        yPos += 10;
      }
      
      // Legal Basis
      checkNewPage(30);
      doc.setFillColor(241, 245, 249);
      doc.rect(margin, yPos, pageWidth - 2 * margin, 25, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('BASE LEGAL', margin + 5, yPos + 7);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      data.base_legal.forEach((item, i) => {
        doc.text(`• ${item}`, margin + 5, yPos + 12 + (i * 4));
      });
      yPos += 32;
      
      // Methodology Note
      checkNewPage(25);
      doc.setFillColor(254, 252, 232);
      doc.rect(margin, yPos, pageWidth - 2 * margin, 20, 'F');
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8);
      const noteLines = doc.splitTextToSize(data.nota_metodologica, pageWidth - 2 * margin - 10);
      noteLines.forEach((line, i) => {
        doc.text(line, margin + 5, yPos + 6 + (i * 4));
      });
      
      // Footer on all pages
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(128, 128, 128);
        doc.text(`Página ${i} de ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
        doc.text('G2eGente - Plataforma de Gestão de Pessoas', pageWidth / 2, pageHeight - 5, { align: 'center' });
      }
      
      // Save PDF
      const fileName = `NR01_PGR_${data.empresa.nome.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
      toast.success('Relatório PDF exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      toast.error('Erro ao exportar relatório PDF');
    } finally {
      setExportingPDF(false);
    }
  };

  const getRiskIcon = (nivel) => {
    switch (nivel) {
      case 'baixo': return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      case 'medio': return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'alto': return <AlertTriangle className="w-5 h-5 text-red-500" />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  if (!results) return null;

  const { diagnostic, total_respostas, total_elegivel, taxa_adesao, pode_exibir, analise_dimensoes, analise_setores, dimensoes_info } = results;

  // Prepare radar data
  const radarData = Object.entries(analise_dimensoes || {}).map(([key, val]) => ({
    dimensao: val.nome,
    media: val.media,
    fullMark: 5
  }));

  // Prepare bar data for dimensions
  const barData = Object.entries(analise_dimensoes || {}).map(([key, val]) => ({
    nome: val.nome,
    media: val.media,
    nivel: val.nivel_risco,
    fill: RISK_COLORS[val.nivel_risco]
  }));

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/company/nr01')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-amber-600" />
            Resultados do Diagnóstico
          </h1>
          <p className="text-slate-500">{diagnostic.titulo}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline"
            onClick={() => navigate(`/company/nr01/${diagnosticId}/psicologo`)}
            className="border-purple-300 text-purple-600 hover:bg-purple-50"
            data-testid="psychologist-area-btn"
          >
            <Brain className="w-4 h-4 mr-2" />
            Área do Psicólogo
          </Button>
          {pode_exibir && (
            <Button 
              variant="outline"
              onClick={handleExportPDF}
              disabled={exportingPDF}
              data-testid="export-pgr-btn"
            >
              {exportingPDF ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Exportar PGR
            </Button>
          )}
          <Badge className={diagnostic.status === 'ativo' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}>
            {diagnostic.status === 'ativo' ? 'Ativo' : 'Encerrado'}
          </Badge>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{total_respostas}</p>
                <p className="text-sm text-slate-500">Respostas</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <Percent className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{taxa_adesao}%</p>
                <p className="text-sm text-slate-500">Adesão</p>
              </div>
            </div>
            <Progress value={taxa_adesao} className="mt-3" />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{diagnostic.setores?.length || 0}</p>
                <p className="text-sm text-slate-500">Setores</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                <ShieldCheck className="w-6 h-6 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{total_elegivel}</p>
                <p className="text-sm text-slate-500">Elegíveis</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Not enough responses warning */}
      {!pode_exibir && (
        <Card className="mb-6 bg-amber-50 border-amber-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600" />
              <div>
                <h4 className="font-semibold text-amber-800">Aguardando mais respostas</h4>
                <p className="text-sm text-amber-700">
                  Os gráficos serão exibidos quando houver pelo menos 2 respostas. 
                  Atualmente há {total_respostas} resposta(s).
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {pode_exibir && (
        <Tabs defaultValue="geral" className="space-y-6">
          <TabsList>
            <TabsTrigger value="geral">Visão Geral</TabsTrigger>
            <TabsTrigger value="setores">Por Setor</TabsTrigger>
            <TabsTrigger value="recomendacoes">Resultados</TabsTrigger>
          </TabsList>

          {/* Tab: Visão Geral */}
          <TabsContent value="geral" className="space-y-6">
            {/* Radar Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Mapa de Riscos por Dimensão</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="dimensao" tick={{ fontSize: 12 }} />
                      <PolarRadiusAxis domain={[0, 5]} />
                      <Radar
                        name="Média"
                        dataKey="media"
                        stroke="#f59e0b"
                        fill="#f59e0b"
                        fillOpacity={0.5}
                      />
                      <Tooltip formatter={(value) => [value.toFixed(2), 'Média']} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Bar Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Médias por Dimensão</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={barData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[0, 5]} />
                      <YAxis type="category" dataKey="nome" width={120} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(value) => [value.toFixed(2), 'Média']} />
                      <Bar dataKey="media" radius={[0, 4, 4, 0]}>
                        {barData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Dimensions Detail */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Análise Detalhada por Dimensão</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {Object.entries(analise_dimensoes || {}).map(([key, dim]) => (
                  <div key={key} className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {getRiskIcon(dim.nivel_risco)}
                        <h4 className="font-semibold text-slate-800">{dim.nome}</h4>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge 
                          className={
                            dim.nivel_risco === 'baixo' ? 'bg-emerald-100 text-emerald-700' :
                            dim.nivel_risco === 'medio' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }
                        >
                          Risco {RISK_LABELS[dim.nivel_risco]}
                        </Badge>
                        <span className="text-xl font-bold" style={{ color: RISK_COLORS[dim.nivel_risco] }}>
                          {dim.media.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-slate-500">{dim.descricao}</p>
                    <div className="mt-2 text-xs text-slate-400">
                      Benchmark: Baixo ≤ {dim.benchmarks.baixo} | Alto ≥ {dim.benchmarks.alto}
                      {dim.invertida && ' (escala invertida)'}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Por Setor */}
          <TabsContent value="setores" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Mapa de Calor por Setor</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 font-semibold">Setor</th>
                        <th className="text-center p-3 font-semibold">Respostas</th>
                        {dimensoes_info.map(dim => (
                          <th key={dim.id} className="text-center p-3 font-semibold" title={dim.descricao}>
                            {dim.nome.substring(0, 8)}...
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(analise_setores || {}).map(([setor, data]) => (
                        <tr key={setor} className="border-b hover:bg-slate-50">
                          <td className="p-3 font-medium">{setor}</td>
                          <td className="text-center p-3">{data.total_respostas}</td>
                          {dimensoes_info.map(dim => {
                            const dimData = data.dimensoes[dim.id];
                            return (
                              <td 
                                key={dim.id} 
                                className="text-center p-3"
                                style={{ 
                                  backgroundColor: dimData ? `${RISK_COLORS[dimData.nivel_risco]}20` : 'transparent'
                                }}
                              >
                                {dimData ? (
                                  <span style={{ color: RISK_COLORS[dimData.nivel_risco], fontWeight: 600 }}>
                                    {dimData.media.toFixed(1)}
                                  </span>
                                ) : '-'}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div className="flex gap-4 mt-4 justify-center">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: `${RISK_COLORS.baixo}40` }} />
                    <span className="text-sm text-slate-600">Baixo Risco</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: `${RISK_COLORS.medio}40` }} />
                    <span className="text-sm text-slate-600">Médio Risco</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: `${RISK_COLORS.alto}40` }} />
                    <span className="text-sm text-slate-600">Alto Risco</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Resultados */}
          <TabsContent value="recomendacoes" className="space-y-6">
            {!recommendations ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                    <Brain className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">
                    Gerar Análise com IA
                  </h3>
                  <p className="text-slate-500 text-center max-w-md mb-4">
                    Nossa IA vai analisar os resultados e gerar recomendações personalizadas 
                    para cada setor com risco médio ou alto, incluindo plano de ação 5W2H.
                  </p>
                  <Button 
                    onClick={handleGenerateRecommendations}
                    disabled={generatingAI}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {generatingAI ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Gerando análise...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Gerar Análise
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Update Analysis Button - Top */}
                <div className="flex justify-end">
                  <Button 
                    variant="outline"
                    onClick={handleGenerateRecommendations}
                    disabled={generatingAI}
                    className="border-purple-300 text-purple-600 hover:bg-purple-50"
                  >
                    {generatingAI ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-2" />
                    )}
                    Atualizar Análise
                  </Button>
                </div>

                {/* Executive Summary */}
                {recommendations.resumo_executivo && (
                  <Card className="bg-purple-50 border-purple-200">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg text-purple-800">
                        <Brain className="w-5 h-5" />
                        Resumo Executivo
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-purple-900">{recommendations.resumo_executivo}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Recommendations per Sector */}
                {recommendations.recomendacoes_por_setor && 
                  Object.entries(recommendations.recomendacoes_por_setor).map(([setor, data]) => (
                    <Card key={setor}>
                      <CardHeader>
                        <CardTitle className="flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            <Building2 className="w-5 h-5 text-amber-600" />
                            {setor}
                          </span>
                          <Badge className={
                            data.nivel_risco_geral === 'alto' ? 'bg-red-100 text-red-700' :
                            data.nivel_risco_geral === 'medio' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-emerald-100 text-emerald-700'
                          }>
                            Risco {data.nivel_risco_geral?.toUpperCase()}
                          </Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {data.principais_riscos && (
                          <div>
                            <h4 className="font-semibold text-slate-700 mb-2">Principais Riscos:</h4>
                            <div className="flex flex-wrap gap-2">
                              {data.principais_riscos.map((risco, i) => (
                                <Badge key={i} variant="outline" className="text-red-600 border-red-200">
                                  {risco}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {data.acoes && data.acoes.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-slate-700 mb-3">Plano de Ação:</h4>
                            <div className="space-y-3">
                              {data.acoes.map((acao, i) => (
                                <div key={i} className="p-4 bg-slate-50 rounded-lg border-l-4 border-amber-500">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Badge variant="outline">{acao.dimensao}</Badge>
                                  </div>
                                  <p className="font-medium text-slate-800 mb-2">{acao.acao}</p>
                                  <div className="grid grid-cols-2 gap-2 text-sm text-slate-600">
                                    <div><strong>Por quê:</strong> {acao.justificativa}</div>
                                    <div><strong>Responsável:</strong> {acao.responsavel}</div>
                                    <div><strong>Prazo:</strong> {acao.prazo}</div>
                                    <div><strong>Custo:</strong> {acao.custo}</div>
                                    <div className="col-span-2"><strong>Como:</strong> {acao.como}</div>
                                    <div className="col-span-2"><strong>Indicador:</strong> {acao.indicador}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))
                }

                {/* PGR Text */}
                {recommendations.texto_pgr && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <FileText className="w-5 h-5 text-amber-600" />
                        Texto para PGR
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="bg-slate-50 p-4 rounded-lg whitespace-pre-wrap text-sm text-slate-700 font-mono">
                        {recommendations.texto_pgr}
                      </div>
                      <Button 
                        variant="outline" 
                        className="mt-4"
                        onClick={handleExportPDF}
                        disabled={exportingPDF}
                        data-testid="export-pdf-btn"
                      >
                        {exportingPDF ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        Exportar PDF para PGR
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {/* Raw text fallback */}
                {recommendations.texto_raw && !recommendations.resumo_executivo && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Análise Completa</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="whitespace-pre-wrap text-sm text-slate-700">
                        {recommendations.texto_raw}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      )}

      {/* LGPD Notice */}
      <Card className="mt-6 bg-slate-50 border-slate-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <h4 className="font-semibold text-slate-700 mb-1">Confidencialidade e Conformidade</h4>
              <p className="text-sm text-slate-600">
                Todas as respostas são anônimas e focam no ambiente de trabalho, não em indivíduos. 
                Os dados são apresentados de forma agregada em conformidade com a LGPD e NR-01.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
